import { Lap } from './Lap'
import { LapMetadata } from './LapMetadata'
import { LapBounds } from './LapBounds'
import { computeBreakingPoints } from './lapAnalysis'
import { ProcessedTelemetryData } from '../telemetry/processed/ProcessedTelemetryData'
import { TelemetryStatistics } from '../telemetry/processed/TelemetryStatistics'
import { GpsPoint } from '../telemetry/processed/GpsPoint'
import { LeanAnglePoint } from '../telemetry/processed/LeanAngle'
import { AccelerationPoint } from '../telemetry/processed/Acceleration'
import { GForcePoint } from '../telemetry/processed/GForce'
import { RawTelemetryData } from '../telemetry/raw/RawTelemetryData'
import { Device, Weather } from '../../api/apiTypes'
import { createSpatialBuckets } from '../../processors/spatial/spatialProcessing'
import { applyGpsDerivedTier } from '../../processors/gpsDerivedTierRelabel'
import { SENSOR_CONTEXT_MS } from '../../processors/sensorContextWindow'
import {
  calculateSamplingFrequencyHz,
  formatElapsedTime,
} from '../../utils/timeUtils'
import { sustainedPeakAbs } from '../../utils/sustainedPeak'
import { LEAN_PEAK_SUSTAIN_WINDOW_S } from '../../analysis/config/leanDisplay'
import { KMH_PER_MPS } from '../../utils/speedUnits'
import { MS_PER_S } from '../../utils/timeUnits'
import { maxOf, minOf } from '../../utils/extremes'

export interface LapWindow {
  startTime: Date
  finishTime: Date
}

export interface BuildLapInput {
  metadata: LapMetadata
  window: LapWindow
  sessionTelemetry: ProcessedTelemetryData
  sessionRawTelemetry: RawTelemetryData
  device?: Device
  sessionName?: string
  weather?: Weather
}

interface TimedPoint {
  timestamp: Date
  elapsedMs: number
  distanceMeters: number
}

interface IndexRange {
  start: number
  end: number
}

interface LapFrame {
  startMs: number
  finishMs: number
  originDistance: number
  finishDistance: number
}

/**
 * The single place a Lap is assembled from session telemetry. Every entry
 * point (pipeline lap stage, finish-line re-detection, shared-lap DTO) hands
 * its lap window here so per-lap rules live in one function.
 *
 * The window runs from finish-line crossing to finish-line crossing, so the
 * lap's time and distance origin is the crossing itself, not the first GPS
 * sample after it.
 */
export function buildLap(input: BuildLapInput): Lap | null {
  const { metadata, window, sessionTelemetry, sessionRawTelemetry } = input
  const range = gpsIndexRange(sessionTelemetry.gpsPoints, window)
  if (!range) return null

  const frame = lapFrame(sessionTelemetry.gpsPoints, range, window)
  const processedTelemetry = sliceProcessedTelemetry(
    sessionTelemetry,
    range,
    frame
  )
  const rawTelemetry = sliceRawTelemetry(sessionRawTelemetry, window)
  const inLap = between(window.startTime.getTime(), window.finishTime.getTime())
  const bounds: LapBounds = {
    startTime: window.startTime,
    finishTime: window.finishTime,
    startDistanceMeters: 0,
    finishDistanceMeters: frame.finishDistance,
  }

  const lap = new Lap(metadata, bounds, processedTelemetry, rawTelemetry, {
    device: input.device,
    sessionName: input.sessionName,
    weather: input.weather,
    gyro: sessionRawTelemetry.gyroscope?.filter(inLap),
    accl: sessionRawTelemetry.accelerometer?.filter(inLap),
    spatialBuckets: createSpatialBuckets(processedTelemetry),
  })

  const keyPoints = computeBreakingPoints(lap)
  lap.breakingPoints = keyPoints.breakingPoints
  lap.slowestPoints = keyPoints.slowestPoints
  lap.topSpeed = keyPoints.topSpeed
  return lap
}

function gpsIndexRange(
  gpsPoints: GpsPoint[],
  window: LapWindow
): IndexRange | null {
  const startMs = window.startTime.getTime()
  const finishMs = window.finishTime.getTime()
  let start = -1
  let end = -1
  for (let i = 0; i < gpsPoints.length; i++) {
    const pointMs = gpsPoints[i].timestamp.getTime()
    if (pointMs < startMs) continue
    if (pointMs > finishMs) break
    if (start === -1) start = i
    end = i
  }
  return start === -1 ? null : { start, end }
}

function lapFrame(
  gpsPoints: GpsPoint[],
  range: IndexRange,
  window: LapWindow
): LapFrame {
  const startMs = window.startTime.getTime()
  const finishMs = window.finishTime.getTime()
  const first = gpsPoints[range.start]
  const last = gpsPoints[range.end]
  const originDistance =
    first.distanceMeters -
    metersTravelled(first, first.timestamp.getTime() - startMs)
  const finishDistance =
    last.distanceMeters -
    originDistance +
    metersTravelled(last, finishMs - last.timestamp.getTime())
  return { startMs, finishMs, originDistance, finishDistance }
}

function metersTravelled(point: GpsPoint, durationMs: number): number {
  return (point.speedKmh / KMH_PER_MPS) * (durationMs / MS_PER_S)
}

function sliceProcessedTelemetry(
  session: ProcessedTelemetryData,
  range: IndexRange,
  frame: LapFrame
): ProcessedTelemetryData {
  const slice = <T extends TimedPoint>(points: T[]) =>
    rebase(points.slice(range.start, range.end + 1), frame)

  const gpsPoints = slice(session.gpsPoints).map((point) => ({
    ...point,
    timeString: formatElapsedTime(point.elapsedMs),
  }))
  const leanAngle = slice(session.leanAngle)
  const acceleration = slice(session.acceleration)
  const gForce = slice(session.gForce)

  applyGpsDerivedTier(acceleration, gpsPoints)

  return {
    gpsPoints,
    leanAngle,
    acceleration,
    gForce,
    statistics: lapStatistics(
      gpsPoints,
      leanAngle,
      acceleration,
      gForce,
      frame
    ),
    diagnostics: session.diagnostics,
  }
}

function rebase<T extends TimedPoint>(points: T[], frame: LapFrame): T[] {
  return points.map((point) => ({
    ...point,
    elapsedMs: point.timestamp.getTime() - frame.startMs,
    distanceMeters: point.distanceMeters - frame.originDistance,
  }))
}

function between(startMs: number, finishMs: number) {
  return <T extends { timestamp: Date }>(reading: T) => {
    const ms = reading.timestamp.getTime()
    return ms >= startMs && ms <= finishMs
  }
}

function sliceRawTelemetry(
  session: RawTelemetryData,
  window: LapWindow
): RawTelemetryData {
  const inContext = between(
    window.startTime.getTime() - SENSOR_CONTEXT_MS,
    window.finishTime.getTime() + SENSOR_CONTEXT_MS
  )
  const gpsReadings = session.gps.filter(inContext)
  const startDistance = gpsReadings[0]?.distanceMeters ?? 0

  return {
    gps: gpsReadings.map((reading) => ({
      ...reading,
      distanceMeters:
        reading.distanceMeters !== undefined
          ? reading.distanceMeters - startDistance
          : undefined,
    })),
    gyroscope: session.gyroscope?.filter(inContext),
    accelerometer: session.accelerometer?.filter(inContext),
    recordingStartTime: gpsReadings[0]?.timestamp ?? window.startTime,
    device: session.device,
  }
}

function lapStatistics(
  gpsPoints: GpsPoint[],
  leanAngle: LeanAnglePoint[],
  acceleration: AccelerationPoint[],
  gForce: GForcePoint[],
  frame: LapFrame
): TelemetryStatistics {
  const speeds = gpsPoints.map((p) => p.speedKmh)
  const longitudinal = acceleration.map((p) => p.longitudinalMs2)

  return {
    maxSpeedKmh: round2(maxOf(speeds)),
    avgSpeedKmh: round2(speeds.reduce((sum, s) => sum + s, 0) / speeds.length),
    totalDistanceMeters: Math.round(frame.finishDistance),
    totalDurationMs: frame.finishMs - frame.startMs,
    dataPointCount: gpsPoints.length,
    samplingFrequencyHz:
      Math.round(calculateSamplingFrequencyHz(gpsPoints) * 10) / 10,
    maxLeanAngle:
      leanAngle.length > 0
        ? round2(
            sustainedPeakAbs(
              leanAngle.map((p) => p.leanAngleDegrees),
              leanAngle.map((p) => p.timestamp.getTime()),
              LEAN_PEAK_SUSTAIN_WINDOW_S * MS_PER_S
            )
          )
        : 0,
    maxLongitudinalG: largestOrZero(
      gForce.map((p) => Math.abs(p.longitudinalG))
    ),
    maxLateralG: largestOrZero(gForce.map((p) => Math.abs(p.lateralG))),
    maxCombinedG: largestOrZero(gForce.map((p) => p.combinedG)),
    maxAccelerationMs2: largestOrZero(longitudinal.filter((v) => v > 0)),
    maxBrakingMs2: Math.abs(
      Math.min(minOf(longitudinal.filter((v) => v < 0)), 0)
    ),
  }
}

function largestOrZero(values: number[]): number {
  return values.length > 0 ? maxOf(values) : 0
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}
